/**
 * ConfidenceGainOverlay
 *
 * Tuile « ceux qui comptent sur vous » : portrait, nom, crans, mention.
 * Les points d'alliance (1–3) animent un cran ; plus de jauge en pourcentages.
 */

import { memo, useEffect, useRef, useState } from 'react';
import type { CharacterPortrait, TrustLabels } from '../types';
import {
  resolveDisplayCran,
  resolveTrustLabel,
  splitCharacterDisplayName,
  TRUST_LOSS_LABEL,
} from '../systems/ally-trust';
import styles from './confidence-gain-overlay.module.css';

interface ConfidenceCharacter {
  characterId: string;
  confidenceLevel: number;
}

interface ConfidenceGainOverlayProps {
  characters: ConfidenceCharacter[];
  characterData: CharacterPortrait[];
  introText?: string;
  continueText?: string;
  startLevels?: Record<string, number>;
  trustLabels?: TrustLabels;
  onContinue: () => void;
}

const TILE_MS = 2800;
const ZERO_TRUST_LABEL = 'ne vous connaît pas';

function cranClass(i: number, haut: number, perte: boolean): string {
  const classes: string[] = [];
  if (i <= haut) classes.push(perte ? styles.perdu : styles.cranOn);
  if (i === haut) classes.push(perte ? styles.part : styles.neuf);
  return classes.join(' ');
}

export const ConfidenceGainOverlay = memo(({
  characters,
  characterData,
  introText,
  continueText = 'Compris',
  startLevels,
  trustLabels,
  onContinue,
}: ConfidenceGainOverlayProps) => {
  const [index, setIndex] = useState(0);
  const popRef = useRef<HTMLDivElement>(null);
  const completedRef = useRef(false);
  const onContinueRef = useRef(onContinue);
  onContinueRef.current = onContinue;
  const needsContinue = Boolean(introText);
  const char = characters[index];

  useEffect(() => {
    setIndex(0);
    completedRef.current = false;
  }, [characters]);

  useEffect(() => {
    const pop = popRef.current;
    if (!pop || !char) return;
    pop.classList.remove(styles.play);
    void pop.offsetWidth;
    pop.classList.add(styles.play);
  }, [char, index]);

  useEffect(() => {
    if (needsContinue || characters.length === 0) return;

    const timer = window.setTimeout(() => {
      if (index < characters.length - 1) {
        setIndex((i) => i + 1);
        return;
      }
      if (completedRef.current) return;
      completedRef.current = true;
      onContinueRef.current();
    }, TILE_MS);

    return () => window.clearTimeout(timer);
  }, [index, characters.length, needsContinue]);

  if (!char) return null;

  const portrait = characterData.find((c) => c.id === char.characterId);
  if (!portrait) return null;

  const startPoints = startLevels?.[char.characterId] ?? 0;
  const afterPoints = char.confidenceLevel;
  const perte = afterPoints < startPoints;
  const apres = resolveDisplayCran(afterPoints);
  const avant = resolveDisplayCran(startPoints);
  const haut = perte ? Math.max(avant, apres) : apres;
  const { givenName, role } = splitCharacterDisplayName(portrait.name);
  const portraitSrc = portrait.icon ?? portrait.defaultImage;
  const mention = perte
    ? TRUST_LOSS_LABEL
    : (apres <= 0 ? ZERO_TRUST_LABEL : resolveTrustLabel(apres, trustLabels));

  return (
    <div className={styles.overlay} role="alert" aria-live="assertive">
      <div className={styles.stage}>
        {introText && (
          <p className={styles.intro}>{introText}</p>
        )}
        <div
          ref={popRef}
          className={`${styles.pop} ${perte ? styles.perte : ''}`.trim()}
        >
          <div className={styles.tuile}>
            <div className={styles.reflet} aria-hidden />
            <div className={styles.rond}>
              {portraitSrc && <img src={portraitSrc} alt="" />}
            </div>
            <div className={styles.nom}>{givenName}</div>
            {role ? <div className={styles.fn}>{role}</div> : null}
            <div className={styles.crans} aria-hidden>
              {[1, 2, 3].map((i) => (
                <i key={i} className={cranClass(i, haut, perte)} />
              ))}
            </div>
            <div className={styles.dit}>{mention}</div>
          </div>
        </div>
        {needsContinue && (
          <button
            type="button"
            className={styles.continueButton}
            onClick={onContinue}
            aria-label={continueText}
          >
            {continueText}
          </button>
        )}
      </div>
    </div>
  );
});
